// This hook loads the standalone POD product preview script only when the caller needs it.

// Use React tools we need for the preview helpers.
import { useCallback, useMemo } from "react";

// Reuse the shared script loader so POD product preview follows the same external module pattern.
import { useExternalScript } from "./useExternalScript";

// Import the stored app config getter.
import { GetAppConfig } from "global/config/AppConfigStore";

// Use the standalone POD product preview module by default, with room for a runtime override later.
const DEFAULT_POD_PRODUCT_PREVIEW_URL =
    "https://v6.ellep.co.uk/assets_modules/modules/pod_product_preview/pod_product_preview.js";

// Reuse one cache-busting token for the whole page load so the script is refreshed once without loading multiple copies.
const POD_PRODUCT_PREVIEW_CACHE_BUST = Date.now();

// Give the app one simple way to use the shared POD product preview module.
export function usePodProductPreview({
    enabled = false,
} = {}) {

    // Read the loaded runtime config only when this hook is called.
    const appConfig = GetAppConfig();

    // Prefer a configured URL, but keep the standalone preview script as the safe default.
    const podProductPreviewUrl =
        appConfig?.podProductPreviewUrl ||
        appConfig?.pod_product_preview_url ||
        DEFAULT_POD_PRODUCT_PREVIEW_URL;

    // Add one stable cache buster so preview loads do not get stuck on an older browser-cached script.
    const cacheBustedPodProductPreviewUrl = useMemo(() => {

        // Stop when there is no configured script URL.
        if (!podProductPreviewUrl) {
            return "";
        }

        // Reuse one stable cache-busting token for this page load.
        const separator = podProductPreviewUrl.includes("?") ? "&" : "?";
        return `${podProductPreviewUrl}${separator}v=${POD_PRODUCT_PREVIEW_CACHE_BUST}`;

    }, [podProductPreviewUrl]);

    // Only load the shared script when the caller actually needs the preview module.
    const status = useExternalScript(enabled ? cacheBustedPodProductPreviewUrl : "");

    // Read the current preview API from the shared Strich namespace.
    const podProductPreview = window.strich?.pod_product_preview || null;

    // Build one preview HTML string through the shared preview API.
    const renderProductPreview = useCallback((design, product, mockupHeight = 100, options = {}) => {

        // Stop here if the preview hook is disabled for this render path.
        if (!enabled) {
            throw new Error("POD product preview is disabled");
        }

        // Stop here if the script is not ready yet.
        if (status !== "ready") {
            throw new Error("POD product preview script not ready");
        }

        // Stop here if the preview renderer is missing on the page.
        if (typeof window.strich?.pod_product_preview !== "function") {
            throw new Error("POD product preview not available on window");
        }

        // Let the shared module render the preview markup.
        return window.strich.pod_product_preview(design, product, mockupHeight, options);

    }, [enabled, status]);

    // Expose the shared preview helpers and simple ready state flags.
    return {

        // The shared preview API on window when it is available.
        podProductPreview,

        // Main way to render preview HTML through the shared module.
        renderProductPreview,

        // True when the script has finished loading.
        isReady: status === "ready",

        // True while the script is still loading.
        isLoading: status === "loading",

        // True when the script failed to load.
        error: status === "error",

        // Expose the raw status as well.
        status,

    };

}
